// Contenu d'une version de démarche côté éditeur : valeur de départ d'un brouillon, libellés des
// états, requêtes partagées (fiche, version, référentiels de la collectivité). Les formes viennent
// des contrats du lot L1-A : rien n'est affiché qui ne soit renvoyé par l'API.
import {
  schemaContenuVersion,
  type ContenuVersion,
  type Demarche,
  type EtapeDemarche,
  type EtatVersionDemarche,
  type PieceDemarche,
} from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';

export interface VersionDemarche {
  id: string;
  numero: number;
  etat: EtatVersionDemarche;
  publiee_le: string | null;
  demarche_id: string;
  contenu: ContenuVersion;
  soumise_le: string | null;
  publiee_par: string | null;
  workflow_definition_id: string | null;
}

export interface Service {
  id: string;
  code: string;
  nom: string;
  parent_id: string | null;
  domaines: string[];
}

export interface TypePiece {
  code: string;
  libelle: string;
  duree_validite_jours: number | null;
  local: boolean;
}

export interface ModeleDocument {
  id: string;
  code: string;
  libelle: string;
  type_document: string;
  version: number;
  publie: boolean;
}

export interface RegleConservation {
  id: string;
  type_document: string;
  duree_annees: number | null;
  sort_final: string;
  commentaire: string | null;
}

export const LIBELLES_ETAT_DEMARCHE: Record<string, string> = {
  brouillon: 'Brouillon',
  en_verification: 'En vérification',
  publiee: 'Publiée',
  suspendue: 'Suspendue',
  retiree: 'Retirée',
};

export const LIBELLES_ETAT_VERSION: Record<string, string> = {
  brouillon: 'Brouillon',
  en_verification: 'En vérification',
  publiee: 'Publiée',
  remplacee: 'Remplacée',
};

export const LIBELLES_NATURE_ETAPE: Record<string, string> = {
  instruction: 'Instruction',
  validation: 'Validation',
  decision: 'Décision',
};

export const LIBELLES_MOMENT_PAIEMENT: Record<string, string> = {
  aucun: 'Aucun paiement (seul moment publiable avant le lot L2)',
  avant_instruction: 'Avant l\'instruction (lot L2)',
  avant_delivrance: 'Avant la délivrance (lot L2)',
};

export const LIBELLES_CANAUX: Record<string, string> = {
  application: 'Application',
  portail: 'Portail citoyen',
  courriel: 'Courriel',
  sms: 'SMS',
};

export const LIBELLES_DOMAINES_DEMARCHE: Record<string, string> = {
  dossier: 'Administration générale',
  marche_municipal: 'Marchés municipaux',
  domaine_public: 'Domaine public',
  evenement: 'Événements',
  urbanisme: 'Urbanisme',
  social: 'Action sociale',
  equipement: 'Équipements',
  patrimoine: 'Patrimoine',
};

export function etapeVide(rang: number, nature: EtapeDemarche['nature'] = 'instruction'): EtapeDemarche {
  return { code: `etape_${rang}`, libelle: '', nature, service_id: null, role_code: null, delai_interne_jours: null, actions: [] };
}

export function pieceVide(): PieceDemarche {
  return { type: '', libelle: '', obligatoire: true };
}

/** Contenu minimal d'une première version : une étape de décision, délais usuels, aucun tarif. */
export function contenuInitial(d: Pick<Demarche, 'libelle' | 'description' | 'domaine'> & { autorite_responsable?: string | null }): ContenuVersion {
  return {
    libelle: d.libelle,
    description: d.description ?? '',
    domaine: d.domaine || 'dossier',
    autorite_responsable: d.autorite_responsable ?? 'Le maire ou le président',
    fondement: 'à confirmer',
    conditions_eligibilite: { texte: '', regles: [] },
    formulaire: { sections: [] },
    pieces: [],
    etapes: [{ code: 'decision', libelle: 'Décision', nature: 'decision', service_id: null, role_code: null, delai_interne_jours: null, actions: [] }],
    delais: { reglementaire: { jours: 30, unite: 'calendaires' }, interne: { jours: 10, unite: 'ouvres' }, relance_complement_jours: 15, classement_sans_suite_jours: 45 },
    tarif: { rubrique: null, montant_indicatif: null, moment_paiement: 'aucun' },
    modele_document_code: null,
    regle_conservation_id: null,
    identite_verifiee_requise: false,
    canaux_notification: ['portail'],
    textes_statuts: {},
  };
}

/**
 * Contenu renvoyé par l'API complété des valeurs par défaut du contrat ; une version créée par
 * l'API sans libellé ni autorité reçoit ceux de la démarche. Le contenu n'est pas validé ici :
 * un brouillon peut être incomplet.
 */
export function normaliserContenu(brut: Record<string, unknown>, demarche: Demarche | undefined): ContenuVersion {
  const base = contenuInitial({ libelle: demarche?.libelle ?? '', description: demarche?.description ?? '', domaine: demarche?.domaine ?? 'dossier' });
  const r = schemaContenuVersion.safeParse({ ...brut, libelle: brut.libelle || base.libelle, autorite_responsable: brut.autorite_responsable || base.autorite_responsable });
  if (r.success) return r.data;
  const b = brut as Partial<ContenuVersion>;
  return {
    ...base,
    ...b,
    libelle: (b.libelle as string) || base.libelle,
    autorite_responsable: (b.autorite_responsable as string) || base.autorite_responsable,
    fondement: (b.fondement as string) || base.fondement,
    conditions_eligibilite: { texte: b.conditions_eligibilite?.texte ?? '', regles: b.conditions_eligibilite?.regles ?? [] },
    formulaire: b.formulaire && Array.isArray(b.formulaire.sections) ? b.formulaire : { sections: [] },
    pieces: Array.isArray(b.pieces) ? b.pieces : [],
    etapes: Array.isArray(b.etapes) && b.etapes.length > 0 ? b.etapes : base.etapes,
    delais: { ...base.delais, ...(b.delais ?? {}) },
    tarif: { ...base.tarif, ...(b.tarif ?? {}) },
    canaux_notification: Array.isArray(b.canaux_notification) && b.canaux_notification.length > 0 ? b.canaux_notification : base.canaux_notification,
    textes_statuts: b.textes_statuts ?? {},
  };
}

/** Problèmes de validation du contrat partagé, par chemin, avant tout envoi au serveur. */
export function problemesContenu(c: ContenuVersion): { chemin: string; message: string }[] {
  const r = schemaContenuVersion.safeParse(c);
  if (r.success) return [];
  return r.error.issues.map((i) => ({ chemin: i.path.map(String).join('.'), message: i.message }));
}

// --- requêtes -------------------------------------------------------------------------------

export function useDemarche(id: string | undefined) {
  return useQuery({
    queryKey: ['demarches', 'fiche', id],
    queryFn: ({ signal }) => requete<Demarche>(`/demarches/${id}`, { signal }),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useVersion(id: string | undefined, numero: number | undefined) {
  return useQuery({
    queryKey: ['demarches', 'version', id, numero],
    queryFn: ({ signal }) => requete<Omit<VersionDemarche, 'contenu'> & { contenu: Record<string, unknown> }>(`/demarches/${id}/versions/${numero}`, { signal }),
    enabled: Boolean(id) && typeof numero === 'number' && numero > 0,
    retry: false,
  });
}

export function useServices() {
  return useQuery({
    queryKey: ['services'],
    queryFn: ({ signal }) => requete<{ elements: Service[] }>('/services', { signal }).then((r) => r.elements),
    staleTime: 60_000,
    retry: false,
  });
}

export function useTypesPieces() {
  return useQuery({
    queryKey: ['types-pieces'],
    queryFn: ({ signal }) => requete<{ elements: TypePiece[] }>('/types-pieces', { signal }).then((r) => r.elements),
    staleTime: 60_000,
    retry: false,
  });
}

export function useModelesDocuments(actif = true) {
  return useQuery({
    queryKey: ['modeles-documents'],
    queryFn: ({ signal }) => requete<{ elements: ModeleDocument[] }>('/modeles-documents', { signal }).then((r) => r.elements),
    enabled: actif,
    staleTime: 60_000,
    retry: false,
  });
}

export function useReglesConservation(actif = true) {
  return useQuery({
    queryKey: ['regles-conservation'],
    queryFn: ({ signal }) => requete<{ elements: RegleConservation[] }>('/regles-conservation', { signal }).then((r) => r.elements),
    enabled: actif,
    staleTime: 60_000,
    retry: false,
  });
}

export function nomService(services: Service[] | undefined, id: string | null | undefined): string {
  if (!id) return 'Service non précisé';
  return services?.find((s) => s.id === id)?.nom ?? `Service ${id.slice(0, 8)}`;
}

export function libelleDelai(d: { jours: number; unite: string } | null): string {
  if (!d) return 'non renseigné';
  const unite = d.unite === 'ouvres' ? 'ouvré' : 'calendaire';
  return `${d.jours} jour${d.jours > 1 ? 's' : ''} ${unite}${d.jours > 1 ? 's' : ''}`;
}
